import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowLeftIcon,
  ClipboardDocumentIcon,
  BackspaceIcon,
  PaperAirplaneIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";

// Dummy data
const chats = [
  {
    id: 1,
    name: "Angel Curtis",
    message: "Please help me find a good monitor for the design",
    time: "02:11",
    unread: 2,
    avatar: "https://i.pravatar.cc/100?img=1",
  },
  {
    id: 2,
    name: "Zaire Dorwart",
    message: "Gacor pisan kang",
    time: "02:11",
    unread: 0,
    avatar: "https://i.pravatar.cc/100?img=2",
  },
  {
    id: 3,
    name: "Kelas Malam",
    message: "Bima : No one can come today?",
    time: "02:11",
    unread: 2,
    avatar: "https://i.pravatar.cc/100?img=3",
  },
];

const reactions = ["🔥", "🙌", "😭", "🙈", "🙏", "😡"];

function ChatList({ onSelect }) {
  return (
    <div className="h-full flex flex-col bg-white">
      <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white z-10">
        <h2 className="text-xl font-semibold">Message</h2>
      </div>

      <div className="flex-1 overflow-y-auto">
        {chats.map((chat) => (
          <button
            key={chat.id}
            className="w-full text-left flex items-center gap-3 p-4 hover:bg-gray-50"
            onClick={() => onSelect(chat)}
          >
            <img
              src={chat.avatar}
              alt="avatar"
              className="w-12 h-12 flex-shrink-0 rounded-full"
            />
            <div className="flex-1 min-w-0">
              <p className="font-semibold leading-tight break-words">{chat.name}</p>
              <p className="text-sm text-gray-500 break-words">{chat.message}</p>
            </div>
            <div className="flex flex-col items-end flex-shrink-0">
              <span className="text-xs text-gray-400">{chat.time}</span>
              {chat.unread > 0 && (
                <span className="mt-1 bg-yellow-400 text-white text-xs px-2 py-0.5 rounded-full">
                  {chat.unread}
                </span>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

function ChatWindow({ chat, onBack }) {
  const [showOptions, setShowOptions] = useState(false);

  return (
    <div className="h-full flex flex-col relative bg-white">
      {/* Header */}
      <div className="flex items-center gap-3 p-4 border-b sticky top-0 bg-white z-10">
        <ArrowLeftIcon className="w-5 h-5 cursor-pointer md:hidden" onClick={onBack} />
        <img src={chat.avatar} alt="avatar" className="w-10 h-10 rounded-full" />
        <div>
          <p className="font-semibold leading-tight">{chat.name}</p>
          <p className="text-xs text-gray-500">Online</p>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 p-4 overflow-y-auto bg-gray-100">
        <div
          className="bg-white shadow rounded-2xl p-3 mb-3 max-w-[80%] break-words cursor-pointer"
          onClick={() => setShowOptions(true)}
        >
          {chat.message}
        </div>
        <div className="ml-auto bg-yellow-100 text-yellow-900 rounded-2xl p-3 mb-3 max-w-[80%] break-words">
          Sure! What size are you thinking?
        </div>
      </div>

      {/* Composer */}
      <div className="p-3 border-t bg-white sticky bottom-0">
        <div className="flex gap-2">
          <input
            placeholder="Type a message"
            className="flex-1 border rounded-full px-4 py-2 focus:outline-none focus:ring-1 focus:ring-gray-300 break-words"
          />
          <button className="bg-black text-white px-4 py-2 rounded-full flex items-center gap-2">
            <PaperAirplaneIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Bottom Sheet (mobile friendly) */}
      {showOptions && (
        <motion.div
          initial={{ y: 48, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="absolute inset-x-0 bottom-0 md:bottom-auto md:top-24 md:right-6 md:left-auto md:w-80 bg-white rounded-t-2xl md:rounded-2xl shadow-xl p-4"
        >
          <div className="mx-auto mb-3 h-1.5 w-10 rounded-full bg-gray-200 md:hidden" />
          <p className="mb-2 font-medium break-words">{chat.message}</p>
          <div className="flex gap-2 mb-4">
            {reactions.map((r, i) => (
              <span key={i} className="text-2xl cursor-pointer active:scale-95">
                {r}
              </span>
            ))}
          </div>
          <div className="flex flex-col gap-3">
            <button className="flex items-center gap-2 text-left">
              <ClipboardDocumentIcon className="w-4 h-4" /> Copy
            </button>
            <button className="flex items-center gap-2 text-left">
              <BackspaceIcon className="w-4 h-4" /> Reply
            </button>
            <button className="flex items-center gap-2 text-left">
              <PaperAirplaneIcon className="w-4 h-4" /> Forward
            </button>
            <button className="flex items-center gap-2 text-left text-red-600">
              <TrashIcon className="w-4 h-4" /> Delete
            </button>
          </div>
          <button
            className="mt-3 w-full py-2 text-gray-500"
            onClick={() => setShowOptions(false)}
          >
            Cancel
          </button>
        </motion.div>
      )}
    </div>
  );
}

export default function ChatUI() {
  const [selectedChat, setSelectedChat] = useState(null);

  return (
    <div className="h-dvh w-full bg-gray-50 overflow-x-hidden">
      {/* Desktop >= md: split layout */}
      <div className="hidden md:grid grid-cols-12 h-full">
        <div className="col-span-4 border-r">
          <ChatList onSelect={setSelectedChat} />
        </div>
        <div className="col-span-8">
          {selectedChat ? (
            <ChatWindow chat={selectedChat} onBack={() => setSelectedChat(null)} />
          ) : (
            <div className="h-full flex items-center justify-center text-gray-400">
              Select a chat
            </div>
          )}
        </div>
      </div>

      {/* Mobile < md: single-pane routing-like UI */}
      <div className="md:hidden h-full">
        {selectedChat ? (
          <ChatWindow chat={selectedChat} onBack={() => setSelectedChat(null)} />
        ) : (
          <ChatList onSelect={setSelectedChat} />
        )}
      </div>
    </div>
  );
}
